import React, { createContext, useContext, useState, useEffect } from "react";
import { Game } from "../types/ScheduleTypes";
import { useDepthChart } from "./DepthChartContext";
import { Side } from "../types/DepthChartTypes";
import { AllSideTabs } from "../constants";
import {
  GameLog,
  KORPlayLog,
  KORPlaySelection,
  OffensivePlayLog,
  OffensivePlaySelection,
  GameSituation,
} from "../types/LogTypes";
import axios from "axios";
import { API_BASE_URL } from "../constants";

interface LoggerContextType {
  selectedGame: Game | null;
  selectedTab: number;
  selectedSide: string;
  gameStarted: boolean;
  gameLog: GameLog | null;
  currentPlayCall: OffensivePlaySelection | KORPlaySelection | null;
  currentPlayLog: OffensivePlayLog | KORPlayLog | null;
  gameSituation: GameSituation;
  touchdown: boolean;
  safety: boolean;

  setSelectedGame: (game: Game | null) => void;
  setSelectedTab: (tab: number) => void;
  setSelectedSide: (side: Side) => void;
  setGameStarted: (gameStarted: boolean) => void;
  onStartGame: (startSide: "KICKOFF" | "KICKOFF_RETURN") => void;
  setGameLog: (gameLog: GameLog | null) => void;
  setCurrentPlayCall: (playCall: OffensivePlaySelection | KORPlaySelection | null) => void;
  setCurrentPlayLog: (playLog: OffensivePlayLog | KORPlayLog | null) => void;
  onSaveLog: (
    playType: Side,
    playCall: OffensivePlaySelection | KORPlaySelection,
    playLog: OffensivePlayLog | KORPlayLog
  ) => void;
  onSaveCurrentPlay: () => void;
  deleteGameLog: () => void;
  setGameSituation: (gameSituation: GameSituation) => void;
  setTouchdown: (touchdown: boolean) => void;
  setSafety: (safety: boolean) => void;
}

export const LoggerContext = createContext<LoggerContextType | undefined>(
  undefined
);

export const LoggerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedSide, setSelectedSide] = useState<Side>("OFFENSE");
  const [gameStarted, setGameStarted] = useState<boolean>(false);
  const [gameLog, setGameLog] = useState<GameLog | null>(null);
  const [currentPlayCall, setCurrentPlayCall] = useState<OffensivePlaySelection | KORPlaySelection | null>(null);
  const [currentPlayLog, setCurrentPlayLog] = useState<OffensivePlayLog | KORPlayLog | null>(null);
  const [touchdown, setTouchdown] = useState<boolean>(false);
  const [safety, setSafety] = useState<boolean>(false);
  const [gameSituation, setGameSituation] = useState<GameSituation>(
    {
      down: -1,
      distance: -1,
      yardline: 50,
      quarter: 0,
      time_remaining: "12:00",
      club_score: 0,
      opp_score: 0,
      club_tol: 3,
      opp_tol: 3,
      club_possession: true,
    }
  );
  const { getDepthChartsForGame } = useDepthChart();

  // Check for existing game log when game is selected but not started
  // This could be because of a refresh - we want to restore the game log
  // and the game started state
  useEffect(() => {
    const checkExistingGameLog = async () => {
      console.log('checking for existing game log', selectedGame?.id, gameStarted);
      if (selectedGame?.id && !gameStarted) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/game-logs/get_game_log/`,
            {
              params: {
                game_id: selectedGame.id,
              },
            }
          );
          if (response.data) {
            console.log('found existing game log', response.data);
            // Found existing game log - restore state
            setGameLog(response.data);
            setGameStarted(true);

            const lastPlay = response.data.logs[response.data.logs.length - 1];
            if (lastPlay) {
              setGameSituation(lastPlay.game_situation);
            }
            getDepthChartsForGame(selectedGame.id);

            console.log("Restored existing game log:", response.data);
          }
        } catch (error) {
          // No existing game log found or error occurred
          console.log("No existing game log found for this game");
        }
      }
    };

    checkExistingGameLog();
  }, [selectedGame, gameStarted]);


  const createGameLog = async () => {
    if (selectedGame?.id) {
      console.log("Creating game log");
      const response = await axios.post(`${API_BASE_URL}/game-logs/`, {
        game_id: selectedGame.id,
      });
      setGameLog(response.data);
    }
  };

  const deleteGameLog = async () => {
    if (gameLog?.id) {
      const response = await axios.delete(`${API_BASE_URL}/game-logs/${gameLog.id}/`);
      setGameLog(null);
      setGameStarted(false);
    }
  };

  const onStartGame = (startSide: "KICKOFF" | "KICKOFF_RETURN") => {
    if (selectedGame?.id) {
      getDepthChartsForGame(selectedGame.id);
    }
    const updatedGameSituation = {
      ...gameSituation,
      club_possession: startSide === "KICKOFF",
      yardline: 40,
      quarter: 1,
    }
    setGameSituation(updatedGameSituation);
    createGameLog();
    setGameStarted(true);
    setSelectedSide(startSide);
    setSelectedTab(AllSideTabs.findIndex((tab) => tab.value === startSide));
  };

  const onSaveLog = async (
    playType: Side,
    playCall: OffensivePlaySelection | KORPlaySelection,
    playLog: OffensivePlayLog | KORPlayLog
  ) => {
    if (gameLog) {
      const response = await axios.post(`${API_BASE_URL}/logs/`, {
        game_log_id: gameLog.id,
        play_type: playType,
        play_call: playCall,
        play_log: playLog,
        game_situation: gameSituation,
        touchdown: touchdown,
        safety: safety,
      });
      console.log("response", response.data);
      setCurrentPlayCall(null);
      setCurrentPlayLog(null);
      setTouchdown(false);
      setSafety(false);
      setGameSituation(response.data.game_situation);
      setSelectedSide(response.data.selected_side);
      setSelectedTab(AllSideTabs.findIndex((tab) => tab.value === response.data.selected_side));
      fetchGameLog();
      
    }
  };

  const onSaveCurrentPlay = () => {
    if (currentPlayCall && currentPlayLog) {
      onSaveLog(selectedSide as Side, currentPlayCall, currentPlayLog);
      
    }
  };

  const fetchGameLog = async () => {
    if (selectedGame?.id) {
      const response = await axios.get(`${API_BASE_URL}/game-logs/get_game_log/`, {
        params: { game_id: selectedGame.id },
      });
      setGameLog(response.data);
    }
  };

  return (
    <LoggerContext.Provider
      value={{
        selectedGame,
        selectedTab,
        selectedSide,
        gameStarted,
        gameLog,
        currentPlayCall,
        currentPlayLog,
        gameSituation,
        touchdown,
        safety,
        setTouchdown,
        setSafety,
        setSelectedGame,
        setSelectedTab,
        setSelectedSide,
        setGameStarted,
        onStartGame,
        setGameLog,
        setCurrentPlayCall,
        setCurrentPlayLog,
        onSaveLog,
        onSaveCurrentPlay,
        deleteGameLog,
        setGameSituation,
      }}
    >
      {children}
    </LoggerContext.Provider>
  );
};

export const useLogger = () => {
  const context = useContext(LoggerContext);
  if (context === undefined) {
    throw new Error("useLogger must be used within a LoggerProvider");
  }
  return context;
};
